import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CityService } from '@city/feature';
import { EnvironmentService } from '@core/feature';
import { TalkMeClientData } from '@main/online-chats/const';
import { OccEndpointsService } from '@spartacus/core';
import { CurrentProfileService } from '@user/feature';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

const talkMe = 'TalkMe';
const GET_CLIENT_ID_TIMEOUT_MS = 1500;
const SEND_TO_OPERATOR_PATH = '/utils/talkme/sendToOperator';

@Injectable({ providedIn: 'root' })
export class TalkMeService {
  public unreadedMessagesCount$ = new BehaviorSubject<number>(0);

  protected isInitialized = false;

  constructor(
    @Inject(DOCUMENT) protected document: Document,
    protected environmentService: EnvironmentService,
    private currentProfileService: CurrentProfileService,
    private cityService: CityService,
    private destroyRef: DestroyRef,
    private occEndpointsService: OccEndpointsService,
  ) {}

  init(): void {
    if (!this.isInitialized && this.environmentService.isPlatformBrowser) {
      window.supportAPIMethod = talkMe;
      const s = this.document.createElement('script');
      s.type = 'text/javascript';
      s.id = 'supportScript';
      s.defer = true;
      const id = '698aff39a41a5e2794c7ad53641c3b8d';
      s.src = 'https://lcab.talk-me.ru/support/support.js?h=' + id;
      const sc = this.document.getElementsByTagName('script')[0];
      window[talkMe] =
        window[talkMe] ||
        function (...args: any[]): void {
          (window[talkMe].q = window[talkMe].q || []).push(args);
        };
      if (sc) sc.parentNode?.insertBefore(s, sc);
      else this.document.documentElement.firstChild?.appendChild(s);

      this.isInitialized = true;

      s.onload = (): void => {
        this.setProfileData();

        if (window[talkMe]) {
          window[talkMe]('setCallback', 'unreadedMessagesCountUpdated', (count: number): void => {
            this.unreadedMessagesCount$.next(count);
          });
        }
      };
    }
  }

  openLiveChat(): void {
    window[talkMe]('openSupport');
    void this.notifyOperatorAboutMessengerClick();
  }

  async notifyOperatorAboutMessengerClick(): Promise<void> {
    this.init();

    try {
      const clientId = await this.getClientId();
      const pageTitle = this.getPageTitle();
      const pageUrl = window.location.href;
      const messageText = `Здравствуйте! Обращение со страницы: ${pageTitle} \n Ссылка: ${pageUrl}`;

      const baseUrl = this.occEndpointsService.getBaseUrl({ prefix: false, baseSite: false });

      await fetch(`${baseUrl}${SEND_TO_OPERATOR_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client: { id: clientId },
          message: { text: messageText, type: 'comment' },
        }),
        keepalive: true,
      });
    } catch (err) {
      console.warn(err);
    }
  }

  private getClientId(): Promise<string | undefined> {
    return new Promise((resolve) => {
      let settled = false;
      const timeoutId = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve(undefined);
        }
      }, GET_CLIENT_ID_TIMEOUT_MS);

      try {
        window[talkMe]('getClientId', (id: string) => {
          if (!settled) {
            settled = true;
            clearTimeout(timeoutId);
            resolve(id);
          }
        });
      } catch {
        if (!settled) {
          settled = true;
          clearTimeout(timeoutId);
          resolve(undefined);
        }
      }
    });
  }

  private getPageTitle(): string {
    const h1Text = this.document.querySelector('h1')?.textContent?.trim();

    return h1Text || this.document.title;
  }

  private setProfileData(): void {
    combineLatest({
      userProfile: this.currentProfileService.get(),
      currentCity: this.cityService.activeBaseStore$,
    })
      .pipe(
        map(({ userProfile, currentCity }) => {
          return {
            name: userProfile.user?.name,
            email: userProfile.user?.email,
            phone: userProfile.user?.phone,
            custom: {
              gorod: currentCity?.name,
            },
          };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: TalkMeClientData): void => {
        window[talkMe]('setClientInfo', res, (err: object | string) => {
          if (err) {
            // eslint-disable-next-line no-console
            console.log('Ошибка setClientInfo', err);
          }
        });
      });
  }
}

declare global {
  interface Window {
    [talkMe]: any;
    supportAPIMethod: string;
  }
}
